import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Gift, DollarSign, Star, TrendingUp, Shield,
  Clock, Smartphone, ShoppingBag, Users, Award, Flame,
  ChevronRight, Share2, Download, BarChart3, PieChart,
} from "lucide-react";
import { API, axios } from "@/api";

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

function StatHero({ icon: Icon, label, value, sub, color }) {
  return (
    <motion.div {...fadeUp} className="rounded-2xl p-4 text-center" style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
      <Icon className="w-6 h-6 mx-auto mb-1" style={{ color }} />
      <p className="text-2xl font-extrabold text-slate-800">{value}</p>
      <p className="text-xs font-semibold mt-0.5" style={{ color }}>{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </motion.div>
  );
}

function BarRow({ label, value, max, color = "#3b82f6" }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-600 font-medium w-28 truncate">{label}</span>
      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-full rounded-full" style={{ background: color }}
        />
      </div>
      <span className="text-xs font-bold text-slate-700 w-8 text-right">{value}</span>
    </div>
  );
}

function DealCard({ deal, rank }) {
  const brandColor = deal.brand_colors?.[0] || "#3b82f6";
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-extrabold flex-shrink-0" style={{ background: brandColor }}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-800 truncate">{deal.name}</p>
        <p className="text-[10px] text-slate-400 truncate">{deal.offer_title || deal.category}</p>
      </div>
      <span className="text-sm font-extrabold text-emerald-600">${deal.offer_value_est}</span>
    </div>
  );
}

export default function AnnualReportPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reports/annual`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleShare = () => {
    const url = window.location.href;
    const text = data ? `${data.year} BirthdayDeaLight Report: ${data.headline.total_deals} deals worth $${data.headline.total_value}!` : "BirthdayDeaLight Annual Report";
    if (navigator.share) {
      navigator.share({ title: text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const h = data.headline;
  const maxCat = Math.max(...data.categories.map(c => c.count));
  const topCats = data.categories.slice(0, 10);
  const catColors = ["#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#84cc16", "#6366f1"];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pb-24" data-testid="annual-report-page">
      {/* Header */}
      <div className="relative px-4 pt-12 pb-8 text-center">
        <button onClick={() => navigate(-1)} className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/10 flex items-center justify-center" data-testid="report-back-btn">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <button onClick={handleShare} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 flex items-center justify-center" data-testid="report-share-btn">
          <Share2 className="w-5 h-5 text-white" />
        </button>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <BarChart3 className="w-10 h-10 text-sky-400 mx-auto mb-3" />
          <h1 className="text-3xl font-extrabold text-white tracking-tight">State of BirthdayDeaLight</h1>
          <p className="text-sky-300 text-base font-semibold mt-1">{data.year} Annual Report</p>
          <p className="text-slate-400 text-xs mt-2">Powered by {h.total_deals} verified deals</p>
        </motion.div>
      </div>

      {/* Hero Stats */}
      <div className="px-4 -mt-2">
        <div className="grid grid-cols-2 gap-2.5">
          <StatHero icon={Gift} label="Total Deals" value={h.total_deals} color="#3b82f6" />
          <StatHero icon={DollarSign} label="Combined Value" value={`$${h.total_value.toLocaleString()}`} color="#10b981" />
          <StatHero icon={TrendingUp} label="Avg Deal Value" value={`$${h.avg_value}`} sub={`Median: $${h.median_value}`} color="#f59e0b" />
          <StatHero icon={Star} label="True Freebies" value={h.true_freebies} sub="No purchase needed" color="#8b5cf6" />
        </div>
      </div>

      {/* Most Valuable Deals */}
      <div className="px-4 mt-6">
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="report-top-value">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-5 h-5 text-orange-500" />
            <h2 className="text-base font-bold text-slate-800">Most Valuable Deals</h2>
          </div>
          {data.top_value_deals.slice(0, 5).map((deal, i) => (
            <DealCard key={deal.business_id} deal={deal} rank={i + 1} />
          ))}
        </div>
      </div>

      {/* Easiest to Claim */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="report-easiest">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-amber-500" />
            <h2 className="text-base font-bold text-slate-800">Easiest to Claim</h2>
          </div>
          {data.easiest_deals.slice(0, 5).map((deal, i) => (
            <div key={deal.business_id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
              <span className="text-xs font-extrabold text-amber-500 w-6">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800 truncate">{deal.name}</p>
                <p className="text-[10px] text-slate-400 truncate">{deal.category}</p>
              </div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className={`w-3 h-3 ${j < deal.ease_stars ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Distribution */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="report-categories">
          <div className="flex items-center gap-2 mb-3">
            <PieChart className="w-5 h-5 text-sky-500" />
            <h2 className="text-base font-bold text-slate-800">Deals by Category</h2>
          </div>
          <div className="space-y-2">
            {topCats.map((cat, i) => (
              <BarRow key={cat.category} label={cat.category} value={cat.count} max={maxCat} color={catColors[i % catColors.length]} />
            ))}
          </div>
        </div>
      </div>

      {/* Requirements Breakdown */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="report-requirements">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-rose-500" />
            <h2 className="text-base font-bold text-slate-800">What's Required?</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
              <Smartphone className="w-5 h-5 text-amber-600 mx-auto mb-1" />
              <p className="text-lg font-extrabold text-slate-800">{data.requirements.app_pct}%</p>
              <p className="text-[10px] text-amber-600 font-semibold">Need an App</p>
              <p className="text-[9px] text-slate-400">{data.requirements.app_required} deals</p>
            </div>
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-center">
              <ShoppingBag className="w-5 h-5 text-rose-600 mx-auto mb-1" />
              <p className="text-lg font-extrabold text-slate-800">{data.requirements.purchase_pct}%</p>
              <p className="text-[10px] text-rose-600 font-semibold">Need a Purchase</p>
              <p className="text-[9px] text-slate-400">{data.requirements.purchase_required} deals</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 text-center">
              <Clock className="w-5 h-5 text-sky-600 mx-auto mb-1" />
              <p className="text-lg font-extrabold text-slate-800">{data.signup_lead.avg_lead_days}d</p>
              <p className="text-[10px] text-sky-600 font-semibold">Avg Signup Lead</p>
              <p className="text-[9px] text-slate-400">{data.signup_lead.deals_needing_advance} deals need advance</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
              <Award className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-lg font-extrabold text-slate-800">{data.redemption_window.avg_window_days}d</p>
              <p className="text-[10px] text-emerald-600 font-semibold">Avg Redemption Window</p>
              <p className="text-[9px] text-slate-400">Max: {data.redemption_window.max_window_days} days</p>
            </div>
          </div>
        </div>
      </div>

      {/* Community */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="report-community">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-violet-500" />
            <h2 className="text-base font-bold text-slate-800">Community</h2>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xl font-extrabold text-slate-800">{data.community.total_users}</p>
              <p className="text-[10px] text-slate-400 font-medium">Users</p>
            </div>
            <div>
              <p className="text-xl font-extrabold text-slate-800">{data.community.total_verifications}</p>
              <p className="text-[10px] text-slate-400 font-medium">Verifications</p>
            </div>
            <div>
              <p className="text-xl font-extrabold text-slate-800">{data.community.total_itineraries}</p>
              <p className="text-[10px] text-slate-400 font-medium">Itineraries</p>
            </div>
          </div>
        </div>
      </div>

      {/* Confidence Distribution */}
      {data.confidence_distribution?.length > 0 && (
        <div className="px-4 mt-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="report-confidence">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-emerald-500" />
              <h2 className="text-base font-bold text-slate-800">Deal Confidence Scores</h2>
            </div>
            <div className="space-y-2">
              {data.confidence_distribution.map((c, i) => (
                <BarRow key={c.range} label={c.range} value={c.count} max={Math.max(...data.confidence_distribution.map(x => x.count))} color="#10b981" />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 mt-6 text-center">
        <p className="text-slate-500 text-[10px]">
          Data as of {new Date(data.generated_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </p>
        <p className="text-slate-600 text-[10px] mt-1">BirthdayDeaLight</p>
      </div>
    </div>
  );
}
